'use client';

import { useState, useEffect, useCallback } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import translations from '../AuctionsScreen/AuctionsScreen.translations';
import { useApiClient } from '../../contexts/ApiContext';
import { useHaptic } from '../../hooks/useHaptic';
import { useConfirmPopup } from '../../hooks/useConfirmPopup';
import { useProfile } from '../../queries/useProfileQueries';
import type { AuctionDetailsResponse, Bid } from '../../services/generated/Api';
import { getMinimumBidAmount, isLastBidderCurrentUser } from './utils';

interface UseBidPlacementOptions {
  auction: AuctionDetailsResponse | null;
  allBids: Bid[];
  currentUserId: string | null;
  onBidPlaced: () => Promise<void>;
}

interface UseBidPlacementResult {
  bidAmount: string;
  setBidAmount: React.Dispatch<React.SetStateAction<string>>;
  isPlacingBid: boolean;
  bidError: string;
  isLastBidderCurrentUser: boolean;
  handlePlaceBid: () => Promise<void>;
}

export const useBidPlacement = ({
  auction,
  allBids,
  currentUserId,
  onBidPlaced,
}: UseBidPlacementOptions): UseBidPlacementResult => {
  const { t } = useLocalTranslation(translations);
  const apiClient = useApiClient();
  const { data: profile } = useProfile();
  const { impact, notification } = useHaptic();
  const showPopup = useConfirmPopup();
  const [bidAmount, setBidAmount] = useState<string>('');
  const [isPlacingBid, setIsPlacingBid] = useState(false);
  const [bidError, setBidError] = useState<string>('');

  // Use stable primitive values instead of object reference
  const auctionCurrentBid = auction?.currentBid;
  const auctionMinBidStep = auction?.minBidStep;

  useEffect(() => {
    if (auctionCurrentBid !== undefined && auction) {
      const minBid = getMinimumBidAmount(auction);
      const currentBid = parseInt(bidAmount) || 0;

      if (!bidAmount || currentBid < minBid) {
        setBidAmount(minBid.toString());
      }
    }
  }, [auctionCurrentBid, auctionMinBidStep]);

  const handlePlaceBid = useCallback(async () => {
    if (!auction || !bidAmount.trim()) return;

    if (allBids.length > 0 && currentUserId && allBids[0].bidder.id === currentUserId) {
      setBidError(t('errorsCannotBidAgain'));
      return;
    }

    const amount = parseInt(bidAmount);
    if (isNaN(amount) || amount <= auction.currentBid) {
      setBidError(t('errorsBidTooLow'));
      return;
    }

    if (profile && amount > profile.balanceInCredits) {
      setBidError(t('errorsInsufficientBalance'));
      return;
    }

    try {
      const result = await showPopup({
        title: t('confirmBidTitle'),
        message: t('confirmBidMessage', {
          amount: amount.toLocaleString('ru-RU'),
        }),
        buttons: [
          { id: 'cancel', type: 'cancel' },
          { id: 'confirm', type: 'default', text: t('confirmBidConfirm') },
        ],
      });

      if (result === 'confirm') {
        impact('heavy');

        setIsPlacingBid(true);
        setBidError('');

        try {
          console.log('Placing bid on auction:', auction.id, 'amount:', amount);
          await apiClient.api.auctionControllerPlaceBid(auction.id, { amount });
          setBidAmount('');

          notification('success');

          await onBidPlaced();
        } catch (err) {
          const error = err as { error?: { message?: string } };
          notification('error');
          setBidError(error?.error?.message || t('errorsPlacingBid'));
        } finally {
          setIsPlacingBid(false);
        }
      }
    } catch (err) {
      console.error('Popup error:', err);
    }
  }, [auction, bidAmount, allBids, currentUserId, profile, apiClient, t, impact, notification, onBidPlaced, showPopup]);

  return {
    bidAmount,
    setBidAmount,
    isPlacingBid,
    bidError,
    isLastBidderCurrentUser: isLastBidderCurrentUser(allBids, currentUserId),
    handlePlaceBid,
  };
};
